import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDashboardStats } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, Clock, CheckCircle, DollarSign, Plus, TrendingUp, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const Dashboard = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('date'); // date, contractor, type, section, farm, money

  useEffect(() => {
    loadStats();
  }, []);

  // Sort recent activity based on selected criteria
  const sortedActivity = stats?.recent_activity ? [...stats.recent_activity].sort((a, b) => {
    switch (sortBy) {
      case 'date':
        return new Date(b.updated_at) - new Date(a.updated_at);
      case 'contractor':
        return (a.contractor_name || 'Unassigned').localeCompare(b.contractor_name || 'Unassigned');
      case 'type':
        return (a.work_type || '').localeCompare(b.work_type || '');
      case 'section':
        return (a.section_code || '').localeCompare(b.section_code || '');
      case 'farm':
        return (a.farm || '').localeCompare(b.farm || '');
      case 'money':
        return (b.estimated_cost || 0) - (a.estimated_cost || 0);
      default:
        return 0;
    }
  }) : [];

  const loadStats = async () => {
    try {
      const data = await getDashboardStats();
      setStats(data);
    } catch (error) {
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: t('totalWorkOrders'),
      value: stats?.total_work_orders || 0,
      icon: FileText,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
    },
    {
      title: t('inProgress'),
      value: stats?.in_progress || 0,
      icon: Clock,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    {
      title: t('pendingApproval'),
      value: stats?.pending_approval || 0,
      icon: TrendingUp,
      color: 'text-amber-600',
      bgColor: 'bg-amber-100',
    },
    {
      title: t('completed'),
      value: stats?.completed || 0,
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
    },
  ];

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      pending: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      pending_approval: 'bg-amber-100 text-amber-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      awaiting_invoice: 'bg-purple-100 text-purple-800',
      invoiced: 'bg-emerald-100 text-emerald-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold font-heading text-foreground">{t('dashboard')}</h1>
          <p className="text-muted-foreground mt-2">{t('overviewText')}</p>
        </div>
        <Button
          onClick={() => navigate('/work-orders/new')}
          className="rounded-full"
          data-testid="create-work-order-button"
        >
          <Plus className="mr-2 h-4 w-4" />
          {t('newWorkOrder')}
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="stat-card" data-testid={`stat-${stat.title.toLowerCase().replace(/\s/g, '-')}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{stat.title}</p>
                    <p className="text-3xl font-bold font-heading mt-2">{stat.value}</p>
                  </div>
                  <div className={`p-3 rounded-xl ${stat.bgColor}`}>
                    <Icon className={`h-6 w-6 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Total Cost Card */}
      <Card className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium opacity-90">{t('totalCost')}</p>
              <p className="text-4xl font-bold font-heading mt-2">
                ${stats?.total_cost?.toLocaleString() || '0'}
              </p>
            </div>
            <div className="p-4 bg-white/20 rounded-xl backdrop-blur-sm">
              <DollarSign className="h-8 w-8" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading">{t('recentActivity')}</CardTitle>
            <div className="flex items-center gap-2">
              <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-52" data-testid="sort-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date">{t('sortByDate')}</SelectItem>
                  <SelectItem value="contractor">{t('sortByContractor')}</SelectItem>
                  <SelectItem value="type">{t('sortByType')}</SelectItem>
                  <SelectItem value="section">{t('sortBySection')}</SelectItem>
                  <SelectItem value="farm">{t('sortByFarm')}</SelectItem>
                  <SelectItem value="money">{t('sortByAmount')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {sortedActivity && sortedActivity.length > 0 ? (
            <div className="space-y-4">
              {sortedActivity.map((activity) => (
                <div
                  key={activity.id}
                  className="flex items-center justify-between p-4 rounded-lg border hover:bg-accent/50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/work-orders/${activity.id}`)}
                  data-testid={`recent-activity-${activity.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-medium font-heading">{activity.title}</h4>
                      <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-semibold">
                        {activity.farm}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                      {activity.section_code && (
                        <span>Section: {activity.section_code}</span>
                      )}
                      {activity.work_type && (
                        <span>Type: {activity.work_type.replace('_', ' ')}</span>
                      )}
                      {activity.work_type === 'task' && activity.assigned_staff_name && (
                        <span>Staff: {activity.assigned_staff_name}</span>
                      )}
                      {activity.work_type !== 'task' && activity.contractor_name && (
                        <span>Contractor: {activity.contractor_name}</span>
                      )}
                      {activity.estimated_cost > 0 && (
                        <span className="font-semibold">
                          {activity.currency} {activity.estimated_cost.toLocaleString()}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {activity.updated_at
                        ? format(new Date(activity.updated_at), 'MMM d, yyyy h:mm a')
                        : 'N/A'}
                    </p>
                  </div>
                  <span
                    className={`status-badge ${getStatusColor(activity.status)}`}
                    data-testid={`status-${activity.status}`}
                  >
                    {activity.status?.replace('_', ' ')}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t('noRecentActivity')}</p>
              <Button
                onClick={() => navigate('/work-orders/new')}
                variant="outline"
                className="mt-4"
                data-testid="create-first-work-order-button"
              >
                {t('createFirstWorkOrder')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
